import { useEffect, useRef, useState } from 'react';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { useProductPodObserver } from './useProductPodObserver';
import { useChunkedProducts } from './useChunkedProducts';

export const useMoreProductsOrchestrator = (props) => {
  const {
    isContextLoading,
    additionalSearchParams,
    storeId,
    channel,
    maxProductsPerRequest,
    loadedProducts,
    productsToLoad,
    ssrProductRequestCount
  } = props;

  const [queueProcessingComplete, setQueueProcessingComplete] = useState(false);

  const initialLoadedCountRef = useRef(loadedProducts?.length || 0);
  const initialRequestMade = useRef(false);

  if (initialLoadedCountRef.current <= 0) {
    initialLoadedCountRef.current = ssrProductRequestCount;
  }

  const [moreProducts, moreProductsResponse] = useLazyDataModel('moreProducts',
    {
      variables: {
        additionalSearchParams,
        storeId,
        channel: channel?.toUpperCase()
      }
    }
  );

  const [entry, startObserver] = useProductPodObserver();
  const { pop, itemsQueued, itemsRequested } = useChunkedProducts(
    productsToLoad,
    maxProductsPerRequest,
    !isContextLoading
  );

  const renderRequestSyncStatus = {
    inSync: (itemsRequested + initialLoadedCountRef.current) === loadedProducts.length,
    difference: ((itemsRequested + initialLoadedCountRef.current) - loadedProducts.length)
  };

  const loadMore = () => {
    const nextIds = pop();
    if (nextIds?.length <= 0) {
      return;
    }
    moreProducts({ variables: { itemIds: nextIds } });
  };

  const orchestrateProductLoading = () => {
    const hasItemsInQueue = itemsQueued?.length > 0;

    const hasNewProductsLoaded = loadedProducts.length > initialLoadedCountRef.current;
    if (hasNewProductsLoaded && hasItemsInQueue) {
      startObserver();
      return;
    }

    const shouldInitiateFirstLoad = !initialRequestMade.current && !isContextLoading && hasItemsInQueue;
    if (shouldInitiateFirstLoad) {
      initialRequestMade.current = true;
      setTimeout(loadMore, 50);
      return;
    }

    const isProcessComplete = initialRequestMade.current && itemsQueued?.length === 0 && hasNewProductsLoaded;
    if (isProcessComplete) {
      setQueueProcessingComplete(true);
    }
  };

  useEffect(() => {
    if (entry?.isIntersecting && !moreProductsResponse?.loading && !isContextLoading) {
      loadMore();
    }
  }, [entry]);

  useEffect(() => {
    orchestrateProductLoading();
  }, [loadedProducts, itemsQueued, isContextLoading]);

  return [
    moreProductsResponse,
    itemsQueued,
    renderRequestSyncStatus,
    queueProcessingComplete
  ];
};
